import { useContext, useMemo } from 'react'
import { zoneStateAt, type ZoneState } from '@/lib/time'
import { ReferenceContext, type ReferenceContextValue } from './reference-context'
import type { Airport } from './types'

export function useReference(): ReferenceContextValue {
  const ctx = useContext(ReferenceContext)
  if (!ctx) throw new Error('useReference must be used inside <ReferenceDataProvider>')
  return ctx
}

/**
 * The join the Gantt runs on: IATA code + UTC instant -> local clock.
 *
 * `airport.timezone` points at a row in the timezone table, whose rules give
 * the offset in force at that instant. Two things can break the chain, and
 * both return null rather than guessing an offset: an unknown airport, and an
 * airport pointing at a zone the transition table does not carry (the shipped
 * drop has six of those, listed in the manifest).
 */
export function useAirportClock() {
  const { airports, zoneRules } = useReference()

  const byIata = useMemo(() => {
    const index = new Map<string, Airport>()
    for (const airport of airports) {
      // First row wins: 17 IATA codes are claimed twice in the source drop.
      if (airport.iata && !index.has(airport.iata)) index.set(airport.iata, airport)
    }
    return index
  }, [airports])

  return useMemo(
    () => ({
      airportOf(iata: string): Airport | null {
        return byIata.get(iata.toUpperCase()) ?? null
      },
      stateAt(iata: string, utcMs: number): ZoneState | null {
        const airport = byIata.get(iata.toUpperCase())
        if (!airport?.timezone) return null
        const rules = zoneRules[airport.timezone]
        if (!rules) return null
        return zoneStateAt(airport.timezone, rules, utcMs)
      },
    }),
    [byIata, zoneRules],
  )
}
